import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PawPrint, Store, ArrowRight, Loader2, CheckCircle2, RotateCcw } from 'lucide-react';
import { API } from '@/utils/api';

const VARIANTS = [
  {
    key: 'hodowca',
    icon: PawPrint,
    title: 'Demo Hodowcy',
    desc: 'Zobacz panel oczami hodowcy Golden Retrieverów z opublikowaną stroną.',
    bullets: ['Strona hodowli, psy i miot ze szczeniętami', 'Skrzynka z zapytaniami od klientów', 'Statystyki odwiedzin i podpowiedzi asystenta'],
    cta: 'Uruchom demo hodowcy',
  },
  {
    key: 'firma',
    icon: Store,
    title: 'Demo Firmy',
    desc: 'Zobacz panel oczami właściciela salonu groomerskiego z Krakowa.',
    bullets: ['Strona firmy z cennikiem usług', 'Zapytania klientów o terminy', 'Statystyki wyświetleń i kontaktów'],
    cta: 'Uruchom demo firmy',
  },
];

export default function DemoStart() {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const startDemo = async (variant) => {
    if (loading) return;
    setLoading(variant);
    setError('');
    try {
      const res = await axios.post(`${API}/demo/start`, { variant });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/panel';
    } catch (e) {
      setError('Nie udało się uruchomić demo. Spróbuj ponownie za chwilę.');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#060610] text-white px-6 py-16 relative overflow-hidden" data-testid="demo-start-page">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-orange-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Link to="/" className="inline-block text-orange-400 font-bold text-lg mb-6">APISAI</Link>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Wypróbuj panel bez konta</h1>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Wybierz wariant demo i klikaj po prawdziwym panelu wypełnionym przykładowymi danymi.
            Możesz wszystko edytować — dane resetują się przy każdym nowym wejściu.
          </p>
        </div>

        {error && (
          <div className="mb-6 text-center text-red-400 text-sm" data-testid="demo-start-error">{error}</div>
        )}

        <div className="grid sm:grid-cols-2 gap-6">
          {VARIANTS.map((v) => (
            <div key={v.key}
              className="group rounded-2xl border border-white/10 bg-[#0c0c1a]/80 backdrop-blur-xl p-7 hover:border-orange-500/40 transition-colors flex flex-col"
              data-testid={`demo-variant-${v.key}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-5">
                <v.icon className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-lg font-bold">{v.title}</h2>
              <p className="mt-2 text-sm text-zinc-400">{v.desc}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {v.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <button onClick={() => startDemo(v.key)} disabled={!!loading}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white font-semibold transition"
                data-testid={`demo-start-${v.key}-btn`}>
                {loading === v.key
                  ? (<><Loader2 className="w-4 h-4 animate-spin" /> Przygotowujemy dane…</>)
                  : (<>{v.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>)}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 text-sm text-zinc-500">
          <span className="inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Dane przykładowe — resetują się automatycznie przy każdym uruchomieniu demo</span>
          <p>
            Wolisz od razu własną stronę?{' '}
            <Link to="/stworz" className="text-orange-400 hover:text-orange-300 font-medium">Wygeneruj ją za darmo</Link>
          </p>
          <Link to="/" className="text-zinc-600 hover:text-zinc-400 transition">← Wróć na stronę główną</Link>
        </div>
      </div>
    </div>
  );
}
